import React, { useEffect, useState } from "react";
import { App, Button, Card, Empty, Space, Table, Tag, Typography } from "antd";
import { CopyOutlined, DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { API_URL } from "../shared/utils/Util";
import { downloadAsCsv, getFetchT } from "../shared/utils/TypedUtil";
import { useAntdTheme, useFeatureFlags } from "../shared/hooks";
import { useAuth } from "../shared/contexts";
import { ListCampaignsResponse } from "../types/api";
import { Campaign } from "../types/model";
import { getCampaignPaidOrders } from "../features/campaigns/api";
import { CampaignSummaryModal } from "../features/campaigns/CampaignSummaryModal";

const { Title, Text } = Typography;

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const { token } = useAntdTheme();
  const navigate = useNavigate();
  const restaurantId = sessionStorage.getItem("restaurant_id");
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [summaryCampaign, setSummaryCampaign] = useState<Campaign | null>(null);
  const { authState } = useAuth();
  const { isEnabled } = useFeatureFlags(authState.currentRestaurant?.id);
  const creationEnabled = isEnabled("EnableCampaignCreationUI");

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    getFetchT<ListCampaignsResponse>(
      `${API_URL}/v1/r/${restaurantId}/campaigns`,
      (data) => {
        setCampaigns(data.campaigns);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        notification.error({
          message: t("campaigns_load_failed"),
          description: err.toString(),
        });
      }
    );
  }, [notification, restaurantId, t]);

  const getStatus = (campaign: Campaign) => {
    const now = dayjs();
    if (dayjs(campaign.order_deadline).isBefore(now)) {
      return "past";
    }

    if (
      campaign.fulfillment_start_date &&
      campaign.fulfillment_end_date &&
      now.isAfter(dayjs(campaign.fulfillment_start_date).startOf("day")) &&
      now.isBefore(dayjs(campaign.fulfillment_end_date).endOf("day"))
    ) {
      return "active";
    }

    return "scheduled";
  };

  const exportPaidOrders = (campaign: Campaign) => {
    if (!restaurantId) {
      return;
    }

    setExportingId(campaign.id);
    getCampaignPaidOrders(
      restaurantId,
      campaign.id,
      (data) => {
        setExportingId(null);
        if (data.order_items.length === 0) {
          notification.info({ message: t("campaign_no_paid_orders") });
          return;
        }
        downloadAsCsv(
          `${campaign.slug}_paid_orders.csv`,
          data.order_items.map((item) => ({
            pos_order_name: item.pos_order_name,
            customer_name: item.customer_name,
            customer_phone: item.customer_phone,
            item_name: item.item_name,
            item_note: item.item_note,
            quantity: item.quantity,
          }))
        );
      },
      (err) => {
        setExportingId(null);
        notification.error({
          message: t("campaign_export_failed"),
          description: err.toString(),
        });
      }
    );
  };

  const columns = [
    {
      title: t("campaign_name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, campaign: Campaign) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary">/{campaign.slug}</Text>
        </Space>
      ),
    },
    {
      title: t("campaign_status"),
      key: "status",
      render: (_: unknown, campaign: Campaign) => {
        const status = getStatus(campaign);
        return (
          <Tag color={status === "past" ? "default" : status === "active" ? "green" : "blue"}>
            {t(`campaign_status_${status}`)}
          </Tag>
        );
      },
    },
    {
      title: t("order_deadline"),
      dataIndex: "order_deadline",
      key: "order_deadline",
      render: (date: string) => dayjs(date).format("YYYY-MM-DD HH:mm"),
    },
    {
      title: t("fulfillment_date_range"),
      key: "fulfillment_date_range",
      render: (_: unknown, campaign: Campaign) =>
        campaign.fulfillment_start_date && campaign.fulfillment_end_date
          ? `${campaign.fulfillment_start_date} – ${campaign.fulfillment_end_date}`
          : t("not_available"),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, campaign: Campaign) => (
        // Keep clicks on the actions from opening the row's summary dialog.
        <Space onClick={(e) => e.stopPropagation()}>
          {creationEnabled && (
            <Button
              icon={<CopyOutlined />}
              onClick={() => navigate("/admin/campaign-editor", { state: { campaign } })}
            >
              {t("copy_campaign")}
            </Button>
          )}
          <Button
            icon={<DownloadOutlined />}
            loading={exportingId === campaign.id}
            onClick={() => exportPaidOrders(campaign)}
          >
            {t("campaign_export_paid_orders")}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} styles={{ body: { padding: 0 } }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Space align="center" style={{ justifyContent: "space-between", width: "100%" }}>
          <Title level={2} style={{ margin: 0 }}>
            {t("campaigns")}
          </Title>
          {creationEnabled && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/admin/campaign-editor")}
            >
              {t("create_campaign")}
            </Button>
          )}
        </Space>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={campaigns}
          columns={columns}
          locale={{ emptyText: <Empty description={t("no_campaigns")} /> }}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          scroll={{ x: 960 }}
          style={{ borderRadius: token.borderRadiusLG }}
          onRow={(campaign) => ({
            onClick: () => setSummaryCampaign(campaign),
            style: { cursor: "pointer" },
          })}
        />
      </Space>
      {restaurantId && (
        <CampaignSummaryModal
          restaurantId={restaurantId}
          campaign={summaryCampaign}
          onClose={() => setSummaryCampaign(null)}
        />
      )}
    </Card>
  );
};

export default CampaignsPage;
